/**
 * Single source of truth for the shape of editable pricing config.
 *
 * This schema is the contract between:
 *   - the pricing engine (consumes a `PricingConfig` to compute quotes)
 *   - the rest-api admin endpoints (validate publish payloads)
 *   - the dashboard editor (renders inputs, validates client-side)
 *   - the DynamoDB store (immutable v#NNNN rows; lazy-migrated at read time)
 *
 * Discipline rules (also in CLAUDE.md § 1.1):
 *   - Schema changes are ADDITIVE-ONLY. Removals/renames need a dual-read
 *     migration window. Bump `CURRENT_SCHEMA_VERSION` and add a migration
 *     when a new field is introduced.
 *   - Tier arrays must be sorted descending by `minQty` and terminate at 0.
 *     Lookup helpers depend on this — the schema enforces it at the boundary.
 *   - All numeric values are non-negative.
 */
import { z } from 'zod';
const NonNegNum = z.number().min(0);
const NonNegInt = z.number().int().min(0);
const PosInt = z.number().int().positive();
/**
 * Generic builder for a customer-qty-keyed tier array. Each tier has
 * `minQty` (the qty threshold this tier applies to) and a value field
 * keyed by the caller-supplied name (`cpc`, `rate`, `floor`, `setup`,
 * `upcharge`, etc.).
 *
 * Constraints enforced:
 *   - non-empty
 *   - terminating tier has `minQty: 0` (the engine's `tierLookup`
 *     invariant — without it, low-qty inputs throw)
 *   - sorted strictly descending by `minQty` (`tierLookup` walks
 *     top-down and takes the first matching tier)
 *   - each tier's value is non-negative
 *
 * Note: monotonicity of values (cheaper at higher volume) is NOT
 * enforced here — that's a business rule, not a structural rule. It's
 * checked separately at publish time so it can be soft-warned and
 * overridden when the team intentionally inverts a tier.
 */
export function tierSchema(valueKey) {
    // Build the tier shape dynamically. The cast at the boundary preserves
    // structural typing (minQty + valueKey both exist as numbers) while
    // letting `valueKey` flow as a generic. Refinements below cast to a
    // narrow shape so we can read `minQty` without TS contortions.
    const obj = z.object({
        minQty: NonNegInt,
        [valueKey]: NonNegNum,
    });
    return z
        .array(obj)
        .min(1, 'At least one tier required.')
        .refine((arr) => {
        const rows = arr;
        return rows[rows.length - 1]?.minQty === 0;
    }, { message: 'Tier array must terminate at minQty: 0 (engine fallback invariant).' })
        .refine((arr) => {
        const rows = arr;
        for (let i = 1; i < rows.length; i++) {
            const prev = rows[i - 1];
            const curr = rows[i];
            if (!prev || !curr)
                return false;
            if (curr.minQty >= prev.minQty)
                return false;
        }
        return true;
    }, { message: 'Tier array must be strictly descending by minQty.' });
}
// ─────────────────────────────────────────────────────────────────
// Universal
// ─────────────────────────────────────────────────────────────────
export const UniversalConfigSchema = z.object({
    /**
     * Flat per-piece upcharge for 2XL / 3XL on non-SS-priced paths
     * (engine throws for 4XL/5XL on those paths — see
     * `OversizedBlankCostRequiredError`).
     */
    sizeUpcharge: z.object({
        xxl: NonNegNum,
        xxxl: NonNegNum,
    }),
});
// ─────────────────────────────────────────────────────────────────
// Screen Print
// ─────────────────────────────────────────────────────────────────
/** INTERNAL fields are admin-only; the rest-api strips them from staff responses. */
export const ScreenPrintConfigSchema = z.object({
    minQty: PosInt,
    /** INTERNAL — production cost per color per piece. */
    cpcTiers: tierSchema('cpc'),
    /** Customer-facing upcharge per additional color beyond the first. */
    extraColorUpcharge: tierSchema('upcharge'),
    /** Customer-facing upcharge per additional print location beyond the first. */
    locationUpcharge: tierSchema('upcharge'),
    /** INTERNAL — flat internal cost per additional location. */
    internalLocationCost: NonNegNum,
    /** Setup fee, auto-amortized into per-unit sell in B2C; B2B always $0. */
    setupTiers: tierSchema('setup'),
    /** B2B 1-color first-location print rate. */
    wholesalePrintTiers: tierSchema('rate'),
    /** B2C retail 1-color first-location rate by garment color. */
    retailPrintTiers: z.object({
        white: tierSchema('rate'),
        colored: tierSchema('rate'),
    }),
    /** INTERNAL — negotiation floor; engine throws BelowFloorError if sell < floor. */
    floorTiers: tierSchema('floor'),
    darkUnderbase: z.object({
        qtyBreak: PosInt,
        below: NonNegNum,
        atOrAbove: NonNegNum,
    }),
    rushMultipliers: z
        .object({
        none: z.literal(1),
        rush: NonNegNum,
        hot_rush: NonNegNum,
    })
        .refine((m) => m.rush >= 1, { message: 'rush multiplier must be ≥ 1.0' })
        .refine((m) => m.hot_rush >= m.rush, {
        message: 'hot_rush multiplier must be ≥ rush multiplier',
    }),
});
// ─────────────────────────────────────────────────────────────────
// DTF Transfer
// ─────────────────────────────────────────────────────────────────
export const DtfTransferConfigSchema = z.object({
    sheetWidthIn: z.number().positive(),
    internalCostPerSqIn: NonNegNum,
    /**
     * Length-keyed base rate. Last tier's `maxLength` may be `Infinity`
     * (unbounded). Phase 2 will serialize Infinity as a null sentinel at
     * the DDB I/O boundary.
     */
    baseRateTiers: z
        .array(z.object({
        maxLength: z.number().positive(),
        rate: NonNegNum,
    }))
        .min(1),
    b2bMultiplier: NonNegNum,
    b2bMinRate: NonNegNum,
    etsyDefaultRate: NonNegNum,
    etsyMultiplier: NonNegNum,
    floor: z.object({
        b2c: NonNegNum,
        b2b: NonNegNum,
        etsy: NonNegNum,
    }),
});
// ─────────────────────────────────────────────────────────────────
// DTF Press & Press
// ─────────────────────────────────────────────────────────────────
export const DtfPressAndPressConfigSchema = z.object({
    transferCostPerSqIn: NonNegNum,
    laborPerLocationDefault: NonNegNum,
    minSellBeforeLaborBlank: NonNegNum,
    marginTiers: tierSchema('marginPct'),
    marginWarnFloorPct: z.number().min(0).max(100),
    rateWarn: z.object({
        red: NonNegNum,
        amber: NonNegNum,
    }),
});
// ─────────────────────────────────────────────────────────────────
// DTG
// ─────────────────────────────────────────────────────────────────
const DtgPriceLeafSchema = z.object({
    single: NonNegNum,
    double: NonNegNum,
});
export const DtgConfigSchema = z.object({
    hardMaxQty: PosInt,
    softMaxQty: PosInt,
    internalCost: z.object({
        light: NonNegNum,
        dark: NonNegNum,
    }),
    /**
     * Inverted-tier pricing: walked top-down and matched on `qty <= maxQty`.
     * Last tier's `maxQty` is the highest representable qty before the engine
     * throws RouteViolationError (today: 23 — anything 24-47 reuses tier
     * 23 with a soft warning, 48+ throws).
     */
    priceTiers: z
        .array(z.object({
        maxQty: PosInt,
        light: DtgPriceLeafSchema,
        dark: DtgPriceLeafSchema,
    }))
        .min(1),
    marginWarnFloorPct: z.number().min(0).max(100),
});
// ─────────────────────────────────────────────────────────────────
// Embroidery
// ─────────────────────────────────────────────────────────────────
export const EmbroideryConfigSchema = z.object({
    minQty: PosInt,
    baseTiers: z.object({
        lc: tierSchema('rate'),
        hat: tierSchema('rate'),
        back: tierSchema('rate'),
    }),
    complexityMultiplier: z.object({
        simple: z.literal(1),
        standard: NonNegNum,
        complex: NonNegNum,
    }),
    additionalLocationTiers: tierSchema('rate'),
    nameTiers: tierSchema('rate'),
    numberMultiplier: NonNegNum,
    stitchTiers: z.object({
        simpleMaxK: PosInt,
        standardMaxK: PosInt,
        complexRepresentativeK: PosInt,
        standardRepresentativeK: PosInt,
        simpleRepresentativeK: PosInt,
    }),
    costDefaults: z.object({
        costPerThousandStitches: NonNegNum,
        overheadPerPiece: NonNegNum,
        threadPerPiece: NonNegNum,
    }),
    /**
     * Digitizing fees by type. Each entry is a [normalRateToShop, rushRateToShop]
     * tuple. Customer-facing charge = myCost + digiCustomerMarkup (when myCost > 0).
     * Adding a new digitizing type is an additive schema change (bump
     * CURRENT_SCHEMA_VERSION + add migration default).
     */
    digiCosts: z.object({
        none: z.tuple([NonNegNum, NonNegNum]),
        dst: z.tuple([NonNegNum, NonNegNum]),
        lc: z.tuple([NonNegNum, NonNegNum]),
        hat: z.tuple([NonNegNum, NonNegNum]),
        hat3d: z.tuple([NonNegNum, NonNegNum]),
        beanie: z.tuple([NonNegNum, NonNegNum]),
        bigfb: z.tuple([NonNegNum, NonNegNum]),
        lchat: z.tuple([NonNegNum, NonNegNum]),
        combo3: z.tuple([NonNegNum, NonNegNum]),
    }),
    digiCustomerMarkup: NonNegNum,
});
// ─────────────────────────────────────────────────────────────────
// Routing
// ─────────────────────────────────────────────────────────────────
export const RoutingConfigSchema = z.object({
    thresholds: z.object({
        dtgMaxQty: PosInt,
        dtfPpMinForSp: PosInt,
        spSingleSideMaxColorsClear: PosInt,
        doubleSidedVolumeBreak: PosInt,
        anyServiceVolumeBreak: PosInt,
    }),
});
// ─────────────────────────────────────────────────────────────────
// Top-level
// ─────────────────────────────────────────────────────────────────
/** Bump only on additive schema changes; add a migration entry too. */
export const CURRENT_SCHEMA_VERSION = 1;
/**
 * Audit / metadata fields stored alongside every published config.
 * Set by the rest-api admin/publish handler — never written by callers.
 */
export const PricingConfigMetaSchema = z.object({
    schemaVersion: z.number().int().positive(),
    version: z.number().int().positive(),
    parentVersion: z.number().int().nullable(),
    effectiveAt: z.string().datetime(),
    publishedAt: z.string().datetime(),
    publishedBy: z.object({
        sub: z.string(),
        email: z.string(),
    }),
    notes: z.string().max(500),
    /** Names of sanity warnings the publisher explicitly overrode. */
    sanityOverrides: z.array(z.string()),
});
export const PricingConfigBodySchema = z.object({
    universal: UniversalConfigSchema,
    screenPrint: ScreenPrintConfigSchema,
    dtfTransfer: DtfTransferConfigSchema,
    dtfPressAndPress: DtfPressAndPressConfigSchema,
    dtg: DtgConfigSchema,
    embroidery: EmbroideryConfigSchema,
    routing: RoutingConfigSchema,
});
export const PricingConfigSchema = PricingConfigMetaSchema.merge(PricingConfigBodySchema);
//# sourceMappingURL=schema.js.map